import React from 'react';
import { Box, Grid, Paper, Typography, Divider, Table, TableBody, TableRow, TableCell, TableContainer, Link } from '@material-ui/core';

import { Title } from 'app/components';

// reuse these from parent (IMPORTANT)
import ConnectionsTable from '../ConnectionsTable';
import KeywordChip from '../KeywordChip';

// helper (move this if needed)
const isHeading = (p) => {
  if (!p) return false;
  const clean = p.trim();
  if (!clean || clean.length > 120) return false;
  if (clean === clean.toUpperCase()) return true;
  return clean.split(' ').some((w) => w.length > 3 && w === w.toUpperCase());
};

function IndividualMatchDetail({ matchRawResult, classes }) {
  if (!matchRawResult) return null;

  return (
    <>
      <Grid container spacing={2}>
        {/* ───────── KEY INFO ───────── */}
        <Grid item md={6} xs={12}>
          <Paper className={classes.table} style={{ overflow: 'hidden' }}>
            <Title prefix="Key Information" className={classes.title} />

            {/* ── Header: avatar + name + role + PEP badge ── */}
            {matchRawResult && (
              <>
                <Box display="flex" alignItems="center" style={{ padding: '16px', borderBottom: '1px solid rgba(0,0,0,0.08)', gap: 12 }}>
                  <Box
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      background: '#e3f2fd',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                    <Typography style={{ fontWeight: 600, fontSize: 15, color: '#1976d2' }}>
                      {matchRawResult?.detail?.profile?.matching_name
                        ?.split(/[\s,]+/)
                        .filter(Boolean)
                        .map((w) => w[0])
                        .slice(0, 2)
                        .join('')
                        .toUpperCase()}
                    </Typography>
                  </Box>
                  <Box flex={1}>
                    <Typography style={{ fontWeight: 600, fontSize: 15 }}>{matchRawResult?.detail?.profile?.matching_name}</Typography>
                    <Typography style={{ fontSize: 13, opacity: 0.6 }}>{matchRawResult?.lists?.referenceList?.[0]?.primaryOccupation?.title || matchRawResult?.lists?.referenceList?.[0]?.subCategory || ''}</Typography>
                  </Box>
                  <Box display="flex" flexWrap="wrap" justifyContent="flex-end" style={{ gap: 4 }}>
                    {matchRawResult?.aml_types?.map((aml, i) => (
                      <KeywordChip key={i} code={aml} classes={classes} />
                    ))}
                  </Box>
                </Box>

                {/* ── Identity grid ── */}
                <Box style={{ padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                  <Typography style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', opacity: 0.45, textTransform: 'uppercase', marginBottom: 10 }}>Identity</Typography>

                  {matchRawResult?.detail?.profile?.person?.aliases?.length > 0 && (
                    <Box mb={1.5}>
                      <Typography style={{ fontSize: 12, opacity: 0.55, marginBottom: 6 }}>Aliases</Typography>
                      <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                        {matchRawResult.detail.profile.person.aliases.map((alias, i) => (
                          <Box
                            key={i}
                            style={{
                              background: 'rgba(0,0,0,0.04)',
                              border: '0.5px solid rgba(0,0,0,0.1)',
                              borderRadius: 4,
                              padding: '2px 8px',
                              fontSize: 12,
                              color: 'inherit',
                              whiteSpace: 'nowrap',
                            }}>
                            {alias.trim()}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  <Box display="flex" flexWrap="wrap" style={{ gap: '12px 24px' }}>
                    {[
                      { label: 'Gender', value: matchRawResult?.lists?.gender },
                      { label: 'Nationality', value: matchRawResult?.lists?.nationality ? `${matchRawResult.lists.nationality}${matchRawResult.lists.nationalityCode ? ` (${matchRawResult.lists.nationalityCode})` : ''}` : null },
                      { label: 'Jurisdiction', value: matchRawResult?.peps?.jurisdiction },
                      { label: 'Country of residence', value: matchRawResult?.lists?.countryOfResidence },
                      { label: 'Place of birth', value: matchRawResult?.lists?.placeOfBirth },
                      {
                        label: 'Deceased',
                        value: matchRawResult?.peps?.deceased
                          ? `Yes${
                              matchRawResult.peps?.monthOfDeath || matchRawResult.peps?.yearOfDeath || matchRawResult.peps?.dayOfDeath
                                ? ` (${[matchRawResult.peps?.dayOfDeath, matchRawResult.peps?.monthOfDeath, matchRawResult.peps?.yearOfDeath].filter(Boolean).join('/')})`
                                : ''
                            }`
                          : 'No',
                      },
                    ].map(({ label, value }) => (
                      <Box key={label} style={{ minWidth: 100 }}>
                        <Typography style={{ fontSize: 12, opacity: 0.55, marginBottom: 2 }}>{label}</Typography>
                        <Typography style={{ fontSize: 13, opacity: value ? 1 : 0.35 }}>{value || '—'}</Typography>
                      </Box>
                    ))}
                  </Box>

                  {matchRawResult?.detail?.profile?.person?.addresses?.length > 0 && (
                    <Box mt={1.5}>
                      <Typography style={{ fontSize: 12, opacity: 0.55, marginBottom: 2 }}>Address</Typography>
                      {matchRawResult.detail.profile.person.addresses.map((addr, i) => (
                        <Typography key={i} style={{ fontSize: 13 }}>
                          {[addr.city, addr.stateOrRegion, addr.countryOrTerritory].filter(Boolean).join(', ')}
                        </Typography>
                      ))}
                    </Box>
                  )}

                  {matchRawResult?.detail?.profile?.person?.reportedAllegation && (
                    <Box mt={1.5}>
                      <Typography style={{ fontSize: 12, opacity: 0.55, marginBottom: 2 }}>Reported allegation</Typography>
                      <Typography style={{ fontSize: 13 }}>{matchRawResult.detail.profile.person.reportedAllegation}</Typography>
                    </Box>
                  )}
                </Box>

                {/* ── Date of birth ── */}
                <Box style={{ padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                  <Typography style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', opacity: 0.45, textTransform: 'uppercase', marginBottom: 10 }}>Date of birth</Typography>
                  <Box display="flex" style={{ gap: '12px 40px', flexWrap: 'wrap' }}>
                    <Box>
                      <Typography style={{ fontSize: 12, opacity: 0.55, marginBottom: 2 }}>Recorded DOB</Typography>
                      <Typography style={{ fontSize: 13, opacity: matchRawResult?.detail?.profile?.person?.dates_of_birth?.length > 0 ? 1 : 0.35 }}>
                        {matchRawResult?.detail?.profile?.person?.dates_of_birth?.length > 0 ? matchRawResult.detail.profile.person.dates_of_birth.map((d) => d.value).join(', ') : '—'}
                      </Typography>
                    </Box>
                    {matchRawResult?.peps?.otherRecordedDatesOfBirth?.length > 0 && (
                      <Box>
                        <Typography style={{ fontSize: 12, opacity: 0.55, marginBottom: 2 }}>Other recorded DOB</Typography>
                        <Typography style={{ fontSize: 13 }}>{matchRawResult.peps.otherRecordedDatesOfBirth.map((d) => [d.day, d.month, d.year].filter(Boolean).join('/')).join('  ·  ')}</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* ── Identification Information ── */}
                {matchRawResult?.lists?.identificationInformationList?.length > 0 && (
                  <Box style={{ padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                    <Typography
                      style={{
                        fontSize: 11,
                        fontWeight: 600,
                        letterSpacing: '0.06em',
                        opacity: 0.45,
                        textTransform: 'uppercase',
                        marginBottom: 10,
                      }}
                    >
                      Identification Information
                    </Typography>

                    {matchRawResult.lists.identificationInformationList.map((item, index) => (
                      <Box key={index} style={{ marginBottom: 8 }}>
                        <Typography style={{ fontSize: 12, opacity: 0.55 }}>
                          {item.type}
                        </Typography>
                        <Typography style={{ fontSize: 13 }}>
                          {item.number}
                        </Typography>
                        {item.note && (
                          <Typography style={{ fontSize: 12, opacity: 0.7 }}>
                            {item.note}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}

                {matchRawResult.lists.images?.length > 0 && (
                  <>
                    <TableRow>
                      <TableCell>
                        <Typography className={classes.head}>Images</Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {matchRawResult.lists.images
                            .filter((img) => img && !img.includes('47news.jp'))
                            .map((img, i) => (
                              <Box key={i} width={120} height={120} style={{ borderRadius: 6, overflow: 'hidden', background: '#f5f5f5' }}>
                                <img
                                  src={img}
                                  alt={`Image ${i + 1}`}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                  }}
                                />
                              </Box>
                            ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  </>
                )}
                {matchRawResult?.lists?.referenceList?.length > 0 && (
                  <Box style={{ padding: '14px 16px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                    <Typography style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', opacity: 0.45, textTransform: 'uppercase', marginBottom: 10 }}>Risk Classifications</Typography>
                    <Box display="flex" flexDirection="column" style={{ gap: 10 }}>
                      {matchRawResult.lists.referenceList.map((ref, i) => {
                        const listItems = [
                          ...(ref.sanctions || []).map((s) => ({ ...s, type: 'sanctions' })),
                          ...(ref.otherOfficialList || []).map((o) => ({ ...o, type: 'ool' })),
                          ...(ref.specialInterestPersonList || []).map((s) => ({ ...s, type: 'si' })),
                        ];

                        return (
                          <Box
                            key={i}
                            style={{
                              background: 'rgba(0,0,0,0.02)',
                              border: '0.5px solid rgba(0,0,0,0.08)',
                              borderRadius: 6,
                              padding: '10px 12px',
                            }}>
                            {/* Header row: chip + category + status */}
                            <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: listItems.length > 0 ? 8 : 0 }}>
                              <KeywordChip code={ref.keyword} classes={classes} />
                              <Typography style={{ fontSize: 13, flex: 1 }}>{ref.category}</Typography>
                              <Box
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  padding: '2px 8px',
                                  borderRadius: 4,
                                  background: ref.status === 'ACTIVE' ? 'rgba(76,175,80,0.1)' : 'rgba(0,0,0,0.06)',
                                  color: ref.status === 'ACTIVE' ? '#2e7d32' : '#666',
                                }}>
                                {ref.status}
                              </Box>
                            </Box>

                            {/* Sub-category */}
                            {ref.subCategory && <Typography style={{ fontSize: 12, opacity: 0.55, marginBottom: 6 }}>{ref.subCategory}</Typography>}

                            {/* Expanded list entries */}
                            {listItems.length > 0 && (
                              <Box style={{ borderTop: '0.5px solid rgba(0,0,0,0.06)', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {listItems.map((item, j) => (
                                  <Box key={j} display="flex" alignItems="flex-start" style={{ gap: 8 }}>
                                    <Box style={{ width: 4, height: 4, borderRadius: '50%', background: '#999', marginTop: 6, flexShrink: 0 }} />
                                    <Box>
                                      <Typography style={{ fontSize: 12, fontWeight: 600 }}>{item.name}</Typography>

                                      {item.from?.year && (
                                        <Typography style={{ fontSize: 11, opacity: 0.5 }}>
                                          Since {[item.from.day, item.from.month, item.from.year].filter(Boolean).join('/')}
                                          {item.to ? ` – ${[item.to.day, item.to.month, item.to.year].filter(Boolean).join('/')}` : ' – present'}
                                        </Typography>
                                      )}
                                      {item.sources?.length > 0 && (
                                        <Box mt={0.5}>
                                          {item.sources.map((src, k) => {
                                            const isString = typeof src === 'string';
                                            const url = isString ? src : src.sourceName;
                                            const label = isString ? src : [src.sourceName, src.sourceDate, src.sourceLanguage].filter(Boolean).join(' · ');
                                            return url ? (
                                              <a key={k} href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, display: 'block', wordBreak: 'break-all', color: '#1976d2' }}>
                                                {label}
                                              </a>
                                            ) : (
                                              <span key={k} style={{ fontSize: 11, display: 'block', color: '#666' }}>
                                                {label}
                                              </span>
                                            );
                                          })}
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
                {/* ── Roles ── */}
                {matchRawResult?.lists?.referenceList?.length > 0 && (
                  <Box style={{ padding: '14px 16px' }}>
                    <Typography style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', opacity: 0.45, textTransform: 'uppercase', marginBottom: 10 }}>Roles</Typography>
                    <Box display="flex" flexDirection="column" style={{ gap: 10 }}>
                      {matchRawResult.lists.referenceList.map((ref, i) => {
                        const roles = [...(ref.primaryOccupation ? [{ ...ref.primaryOccupation, current: true }] : []), ...(ref.previousRoles || []).map((r) => ({ ...r, current: false }))];
                        return roles.map((role, j) => (
                          <Box key={`${i}-${j}`} display="flex" alignItems="flex-start" style={{ gap: 10 }}>
                            <Box
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                marginTop: 4,
                                flexShrink: 0,
                                background: role.current ? '#4caf50' : '#bdbdbd',
                                border: `2px solid ${role.current ? '#a5d6a7' : '#e0e0e0'}`,
                              }}
                            />
                            <Box>
                              <Typography style={{ fontSize: 13, fontWeight: role.current ? 600 : 400 }}>{role.title}</Typography>
                              <Typography style={{ fontSize: 12, opacity: 0.55 }}>
                                {[role.from?.month, role.from?.year].filter(Boolean).join('/')}
                                {role.to ? ` – ${[role.to.month, role.to.year].filter(Boolean).join('/')}` : ' – present'}
                                {role.category ? `  ·  ${role.category}` : ''}
                              </Typography>
                            </Box>
                          </Box>
                        ));
                      })}
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Paper>
        </Grid>

        {/* ───────── CONNECTIONS ───────── */}
        {(matchRawResult?.lists?.connections?.length > 0 || matchRawResult?.lists?.linkedIndividuals?.length > 0 || matchRawResult?.lists?.linkedBusinesses?.length > 0) && (
          <Grid item md={6} xs={12}>
            <Paper className={classes.table}>
              <Title prefix="Connections & Associates" className={classes.title} />

              <Grid container spacing={2} style={{ padding: 16 }}>
                {matchRawResult?.lists?.connections?.length > 0 && (
                  <Grid item xs={12}>
                    <ConnectionsTable title="Connections" rows={matchRawResult.lists.connections} classes={classes} />
                  </Grid>
                )}

                {matchRawResult?.lists?.linkedIndividuals?.length > 0 && (
                  <Grid item xs={12}>
                    <ConnectionsTable title="Linked Individuals" rows={matchRawResult.lists.linkedIndividuals} classes={classes} />
                  </Grid>
                )}

                {matchRawResult?.lists?.linkedBusinesses?.length > 0 && (
                  <Grid item xs={12}>
                    <ConnectionsTable title="Linked Businesses" rows={matchRawResult.lists.linkedBusinesses} classes={classes} />
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        )}
      </Grid>
      {/* ───────── MEDIA ───────── */}
      {matchRawResult?.media?.length > 0 && (
        <Grid item xs={12}>
          <TableContainer component={Paper} className={classes.table}>
            <Title prefix="Media" className={classes.title} />
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Grid container spacing={2}>
                      {matchRawResult.media.map((item, i) => (
                        <Grid item xs={12} md={6} key={i}>
                          <Box
                            style={{
                              border: '0.5px solid rgba(0,0,0,0.1)',
                              borderRadius: 6,
                              padding: 10,
                            }}>
                            <Link href={item.externalLink} target="_blank" rel="noopener noreferrer">
                              {item.title || item.externalLink}
                            </Link>

                            {item.title && (
                              <Typography variant="caption" display="block" style={{ color: 'gray', wordBreak: 'break-all' }}>
                                {item.externalLink}
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      )}

      {/* ───────── PROFILE NOTES ───────── */}
      {matchRawResult?.detail?.profile?.person?.profileNotes?.length > 0 && (
        <Grid item xs={12}>
          <Paper className={classes.table}>
            <Box style={{ padding: 16 }}>
              {/* TITLE */}
              <Title prefix="Profile Notes" className={classes.title} />

              {/* NOTES */}
              {matchRawResult.peps.profileNotes.map((note, i) => {
                const paragraphs = note
                  .split('\n')
                  .map((p) => p.trim())
                  .filter(Boolean);

                return (
                  <Box key={i} mb={4}>
                    {/* NOTE HEADER */}
                    <Typography
                      style={{
                        fontSize: 16,
                        fontWeight: 600,
                        marginBottom: 12,
                        color: '#9c27b0',
                        borderBottom: '1px solid rgba(0,0,0,0.1)',
                        paddingBottom: 6,
                      }}>
                      Note {i + 1}
                    </Typography>

                    {/* CONTENT */}
                    {paragraphs.map((p, idx) => {
                      const heading = isHeading(p);

                      return (
                        <Box key={idx} mb={1.5}>
                          {/* Divider between sections */}
                          {heading && idx !== 0 && <Divider style={{ margin: '12px 0' }} />}

                          <Typography
                            style={{
                              fontWeight: heading ? 600 : 400,
                              fontSize: heading ? 13 : 14,
                              color: heading ? '#1976d2' : '#333',
                              textTransform: heading ? 'uppercase' : 'none',
                              letterSpacing: heading ? '0.04em' : 'normal',
                              lineHeight: 1.6,
                            }}>
                            {p}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Grid>
      )}
    </>
  );
}

export default IndividualMatchDetail;
